<script setup>
import { reactive, watch } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import { updateBankTransferInfo } from '@/api/products/orders';

const props = defineProps({
    open: Boolean,
    order: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update:open', 'success']);

const form = reactive({
    transfer_date: null,
    transfer_bank: '',
    account_name: '',
    account_last_5: ''
});

const loading = reactive({
    submit: false
});

// 當 Modal 開啟時，根據現有訂單資料初始化表單
watch(
    () => props.open,
    (newVal) => {
        if (newVal && props.order) {
            form.transfer_date = props.order.transfer_date
                ? dayjs(props.order.transfer_date)
                : null;
            form.transfer_bank = props.order.transfer_bank || '';
            form.account_name = props.order.account_name || '';
            form.account_last_5 = props.order.account_last_5 || '';
        }
    }
);

const handleCancel = () => {
    emit('update:open', false);
};

const handleOk = async () => {
    if (!props.order) return;

    const payload = {};
    if (form.transfer_date) {
        payload.transfer_date = form.transfer_date.format('YYYY-MM-DD');
    }
    if (form.transfer_bank?.trim()) {
        payload.transfer_bank = form.transfer_bank.trim();
    }
    if (form.account_name?.trim()) {
        payload.account_name = form.account_name.trim();
    }
    if (form.account_last_5?.trim()) {
        const v = form.account_last_5.trim();
        if (v.length !== 5) {
            message.warning('匯款帳號後五碼必須為 5 碼');
            return;
        }
        payload.account_last_5 = v;
    }

    if (Object.keys(payload).length === 0) {
        message.warning('請至少輸入一個匯款欄位');
        return;
    }

    try {
        loading.submit = true;
        const res = await updateBankTransferInfo(props.order.id, payload);
        if (res.result) {
            message.success('匯款資料更新成功');
            emit('success');
            emit('update:open', false);
        }
    } catch (err) {
        message.error(err?.response?.data?.message || '更新失敗');
    } finally {
        loading.submit = false;
    }
};
</script>

<template>
    <a-modal
        :open="open"
        title="編輯匯款資料"
        :width="480"
        :confirm-loading="loading.submit"
        ok-text="儲存"
        cancel-text="取消"
        @ok="handleOk"
        @cancel="handleCancel"
        centered
        class="vaporwave-modal"
    >
        <a-form layout="vertical" class="mt-4">
            <a-form-item label="匯款日期">
                <a-date-picker
                    v-model:value="form.transfer_date"
                    class="w-full"
                    format="YYYY-MM-DD"
                    placeholder="選擇匯款日期"
                />
            </a-form-item>
            <a-form-item label="匯款銀行">
                <a-input
                    v-model:value="form.transfer_bank"
                    placeholder="例如：822 中國信託"
                    :maxlength="50"
                />
            </a-form-item>
            <a-form-item label="匯款帳號戶名">
                <a-input
                    v-model:value="form.account_name"
                    placeholder="請輸入戶名"
                    :maxlength="50"
                />
            </a-form-item>
            <a-form-item label="匯款帳號後五碼">
                <a-input
                    v-model:value="form.account_last_5"
                    placeholder="請輸入 5 碼數字"
                    :maxlength="5"
                />
            </a-form-item>
        </a-form>
    </a-modal>
</template>

<style scoped>
:deep(.ant-form-item-label > label) {
    color: #2a2a3a !important;
    font-weight: 600 !important;
    font-family: 'Courier New', monospace !important;
}
</style>
